import React from 'react';

/**
 * Parses WhatsApp markdown formatting (*bold*, _italic_, ~strike~, ```monospace```, `code`)
 * into stylized React nodes using React.createElement (compatible with Node ESM test runner).
 */
export function formatWhatsAppMessage(text: string): React.ReactNode {
  if (!text) return null;

  const lines = text.split('\n');

  return lines.map((line, lineIdx) =>
    React.createElement(
      React.Fragment,
      { key: lineIdx },
      lineIdx > 0 ? React.createElement('br', { key: `br-${lineIdx}` }) : null,
      parseFormattedLine(line)
    )
  );
}

/**
 * Helper to recursively parse formatted tokens within a single line.
 */
function parseFormattedLine(line: string): React.ReactNode {
  if (!line) return null;

  // Regex matching WhatsApp formatting markers:
  // 1: ```monospace```
  // 2: `inline code`
  // 3: *bold* (must not have space immediately after opening or before closing)
  // 4: _italic_ (must not have space immediately after opening or before closing)
  // 5: ~strikethrough~ (must not have space immediately after opening or before closing)
  const tokenRegex = /(```[\s\S]*?```|`[^`]+?`|\*(?!\s)[^*]+?(?<!\s)\*|_(?!\s)[^_]+?(?<!\s)_|~(?!\s)[^~]+?(?<!\s)~)/g;

  const parts = line.split(tokenRegex);

  return parts.map((part, idx) => {
    if (!part) return null;

    // Triple backticks monospace
    if (part.startsWith('```') && part.endsWith('```') && part.length >= 6) {
      const content = part.slice(3, -3);
      return React.createElement(
        'code',
        {
          key: idx,
          className: 'font-mono bg-black/10 dark:bg-white/10 px-1 py-0.5 rounded text-[11px]',
        },
        content
      );
    }

    // Single backtick inline code
    if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
      const content = part.slice(1, -1);
      return React.createElement(
        'code',
        {
          key: idx,
          className: 'font-mono bg-black/10 dark:bg-white/10 px-1 py-0.5 rounded text-[11px]',
        },
        content
      );
    }

    // Bold (*text*)
    if (part.startsWith('*') && part.endsWith('*') && part.length >= 2) {
      const content = part.slice(1, -1);
      return React.createElement(
        'strong',
        {
          key: idx,
          className: 'font-bold text-inherit',
        },
        parseFormattedLine(content)
      );
    }

    // Italic (_text_)
    if (part.startsWith('_') && part.endsWith('_') && part.length >= 2) {
      const content = part.slice(1, -1);
      return React.createElement(
        'em',
        {
          key: idx,
          className: 'italic text-inherit',
        },
        parseFormattedLine(content)
      );
    }

    // Strikethrough (~text~)
    if (part.startsWith('~') && part.endsWith('~') && part.length >= 2) {
      const content = part.slice(1, -1);
      return React.createElement(
        'span',
        {
          key: idx,
          className: 'line-through text-inherit',
        },
        parseFormattedLine(content)
      );
    }

    return part;
  });
}
